import React, { useEffect, useState, useContext } from "react";
import { toast } from "react-toastify";
import { FaShoppingBag } from "react-icons/fa";
import { ChevronRight } from "lucide-react";
import OrderDetails from "./OrderDetails";
import { fetchOrders } from "../../../services/Profile/Order/Order";
import { UserContext } from "../../../context/UserContext";

import fallbackImg from "../../assets/logo/appstore.png";

export default function MyOrders() {
  const { user: contextUser } = useContext(UserContext);
  const localUser = JSON.parse(localStorage.getItem("medarphaUser") || "{}");
  
  const userId =
    contextUser?.id ||
    contextUser?.userId ||
    contextUser?.UserId ||
    localUser?.id ||
    localUser?.userId ||
    localUser?.UserId;

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  const [openDrawer, setOpenDrawer] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    const finalUserId = userId || 1; // Fallback to demo/first user if not logged in
    
    const loadOrders = async () => {
      setLoading(true);
      const res = await fetchOrders(finalUserId);

      if (res.ok) {
        if (res.data && res.data.length > 0) {
          const formatted = res.data.map((order) => {
            let items = [];
            let totalQty = 0;

            try {
              items = JSON.parse(order.itemsJson || "[]");
              totalQty = items.reduce(
                (sum, item) => sum + (item.Quantity || 0),
                0
              );
            } catch (err) {
              console.error("JSON parse error:", err);
            }

            return {
              ...order,
              items,
              totalQuantity: totalQty || (items.length > 0 ? items.length : 1),
            };
          });

          setOrders(formatted);
        } else {
          setOrders([]);
        }
      } else {
        toast.error(res.error || "Failed to load orders");
      }

      setLoading(false);
    };

    loadOrders();
  }, [userId]);

  const getStatusStyle = (status) => {
    switch (status) {
      case "Delivered":
        return { background: "#e8f5e9", color: "#2e7d32" };
      case "Scheduled":
        return { background: "#fff8e1", color: "#f57c00" };
      case "Cancelled":
        return { background: "#ffebee", color: "#c62828" };
      default:
        return { background: "#e3f2fd", color: "#1565c0" };
    }
  };

  return (
    <div style={{ fontFamily: "'Roboto', sans-serif" }}>

      {/* HEADER */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-5 shadow-sm flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-50 text-red-600 flex items-center justify-center shadow-xs">
            <FaShoppingBag size={20} />
          </div>
          <div>
            <h1 className="text-xl font-black text-slate-900 leading-tight">My Test Bookings & Orders</h1>
            <p className="text-xs text-slate-500 font-medium mt-0.5">Track and review all your lab tests and medical orders</p>
          </div>
        </div>
        <span className="text-xs font-bold bg-slate-100 text-slate-700 px-3 py-1.5 rounded-full border border-slate-200">
          {orders.length} {orders.length === 1 ? "Booking" : "Bookings"}
        </span>
      </div>

      {/* LOADING SKELETONS */}
      {loading ? (
        <div className="flex flex-col gap-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center gap-4 animate-pulse shadow-sm"
            >
              <div className="w-16 h-16 rounded-xl bg-slate-100 flex-shrink-0" />
              <div className="flex-1 space-y-2.5">
                <div className="h-4 bg-slate-100 rounded w-1/3" />
                <div className="h-5 bg-slate-100 rounded w-1/2" />
                <div className="h-3 bg-slate-100 rounded w-1/4" />
              </div>
            </div>
          ))}
        </div>
      ) : orders.length > 0 ? (
        <div className="flex flex-col gap-3">
          {orders.map((order) => (
            <div
              key={order.orderId}
              onClick={() => {
                setSelectedOrder(order);
                setOpenDrawer(true);
              }}
              className="bg-white border border-slate-200 hover:border-red-300 rounded-2xl p-4 sm:p-5 flex items-center gap-4 sm:gap-5 cursor-pointer transition-all duration-200 hover:shadow-md group shadow-xs"
            >
              {/* ICON / THUMB */}
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-red-50 to-rose-100 border border-red-200 flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform text-red-600 font-black">
                <FaShoppingBag size={24} />
              </div>

              {/* CONTENT */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1.5">
                  <span className="font-mono text-xs sm:text-sm font-black text-slate-900 bg-slate-100 px-2.5 py-0.5 rounded-md border border-slate-200">
                    {order.orderNumber || `ORD-#${order.orderId}`}
                  </span>
                  <span className="text-xs text-slate-500 font-semibold bg-slate-50 px-2 py-0.5 rounded border border-slate-200">
                    📅 {new Date(order.orderDate).toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </span>
                </div>

                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <span className="text-xs font-semibold text-slate-600">
                    📦 {order.totalQuantity} {order.totalQuantity === 1 ? "Test/Item" : "Tests/Items"}
                  </span>
                  <span className="text-sm sm:text-base font-black text-slate-900">
                    ₹{(order.finalAmount || 0).toFixed(2)}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <span
                    className="inline-flex items-center text-[11px] font-extrabold px-2.5 py-0.5 rounded-full"
                    style={getStatusStyle(order.status)}
                  >
                    ● {order.status || "Active"}
                  </span>
                  {order.labName && (
                    <span className="text-[11px] text-slate-500 font-medium truncate max-w-[180px]">
                      🏥 {order.labName}
                    </span>
                  )}
                </div>
              </div>

              {/* ACTION */}
              <div className="flex items-center gap-1.5 text-xs font-bold text-red-600 bg-red-50 group-hover:bg-red-600 group-hover:text-white px-3 py-1.5 rounded-xl transition-colors flex-shrink-0">
                <span>Details</span>
                <ChevronRight size={16} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* EMPTY STATE */
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center shadow-sm">
          <div className="w-16 h-16 rounded-full bg-slate-50 border border-slate-200 text-slate-400 flex items-center justify-center mx-auto mb-4 text-2xl">
            <FaShoppingBag />
          </div>
          <h3 className="text-lg font-bold text-slate-800 mb-1">
            No Bookings Found Yet
          </h3>
          <p className="text-xs text-slate-500 max-w-sm mx-auto mb-5 font-medium">
            You haven't placed any test bookings or health packages yet.
          </p>
          <a
            href="/"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-red-600 to-rose-600 text-white font-bold px-5 py-2.5 rounded-xl text-xs shadow-sm hover:shadow-md transition-all"
          >
            Explore Tests & Packages
          </a>
        </div>
      )}

      {/* DRAWER */}
      {openDrawer && (
        <OrderDetails
          order={selectedOrder}
          onClose={() => setOpenDrawer(false)}
        />
      )}

      <style>{`
        @keyframes skPulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.45; }
        }
      `}</style>
    </div>
  );
}
